import React, { FC, useCallback, useState } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import FileTag from "@mapmycustomers/shared/types/entity/FileTag";
import { LoadingSpinner } from "@mapmycustomers/ui";

import Pagination from "@app/component/view/ListView/components/Pagination/Pagination";
import { RootState } from "@app/store/rootReducer";

import { getTotalFileTags, getViewState, isLoading } from "../../store";
import { fetchFileTags } from "../../store/actions";
import { DEFAULT_PAGE_SIZE } from "../../store/saga";
import TagItem from "../TagItem";

import styles from "./TagList.module.scss";
import Toolbar from "./Toolbar";

type ListProps = {
  fileTags: FileTag[];
  onChange: (fileTags: FileTag[]) => void;
} & typeof mapDispatchToProps &
  ReturnType<typeof mapStateToProps>;

const TagsList: FC<ListProps> = ({
  fileTags,
  loading,
  onChange,
  onFetchFileTags,
  total,
  viewState,
}) => {
  const intl = useIntl();
  const [selectedTagIds, setSelectedTagIds] = useState<FileTag["id"][]>([]);

  const { page } = viewState;

  const handleChange = useCallback(
    (updatedTag: FileTag) => {
      onChange(fileTags.map((tag) => (tag.id === updatedTag.id ? updatedTag : tag)));
    },
    [onChange, fileTags]
  );

  const handleSelect = useCallback(
    (tagId: FileTag["id"]) => {
      if (selectedTagIds.includes(tagId)) {
        setSelectedTagIds((ids) => ids.filter((id) => id !== tagId));
      } else {
        setSelectedTagIds((ids) => [...ids, tagId]);
      }
    },
    [selectedTagIds]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      setSelectedTagIds([]);
      onFetchFileTags({ ...viewState, page });
    },
    [onFetchFileTags, viewState]
  );

  return (
    <div className={styles.container}>
      <Toolbar
        fileTags={fileTags}
        onChange={onChange}
        onChangeSelectedTagIds={setSelectedTagIds}
        selectedTagIds={selectedTagIds}
      />
      {!fileTags.length && !loading ? (
        <div className={styles.noTags}>
          {intl.formatMessage({
            id: "settings.fileTags.tags.empty",
            defaultMessage: "No tags found.",
            description: "No tags message",
          })}
        </div>
      ) : (
        <>
          <div className={styles.list}>
            {loading ? (
              <LoadingSpinner />
            ) : (
              fileTags.map((tag) => (
                <TagItem
                  key={tag.id}
                  onChange={handleChange}
                  onSelect={handleSelect}
                  selected={selectedTagIds.includes(tag.id)}
                  tag={tag}
                />
              ))
            )}
          </div>
          <Pagination
            currentPage={page}
            onChange={handlePageChange}
            pageSize={DEFAULT_PAGE_SIZE}
            totalRecords={total}
          />
        </>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  loading: isLoading(state),
  total: getTotalFileTags(state),
  viewState: getViewState(state),
});

const mapDispatchToProps = {
  onFetchFileTags: fetchFileTags.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(TagsList);
